/**
 * MileIQ Scripts
// Usage examples:
// Example 1: Using the ready event
document.addEventListener('MiQ:Telemetry:ready', function(e) {
  console.log('Plugin is ready!', e.detail.instance);
  MiQ.exec('doSomething', 'event listener example');
});
// Example 2: Using the ready event
document.addEventListener('MiQ:Telemetry:ready', function(e) {
  console.log('Plugin is ready!', e.detail.instance);
  MiQ.exec('doSomething', 'event listener example');
});
// Example 3: Queueing commands before ready
MiQ.Telemetry.exec('doSomething', 'queued command');
// Example 4: Checking if ready before executing
function myFunction() {
  if (MiQ.Telemetry.isReady()) {
    MiQ.Telemetry.exec('processData', { key: 'value' }, function(result) {
      console.log('Processed:', result);
    });
  } else {
    // Try again later or use the ready method
    MiQ.Telemetry.ready(function() {
      this.processData({ key: 'value' }, function(result) {
        console.log('Processed:', result);
      });
    });
  }
}
*/

import { getTrackingAttributesFromUrl } from "@/utils";
import { initAvo } from "@/vendors/avo";
import { getImpactClickId } from "@/vendors/impact";
import { logger } from "../logs";

export default (function (window, document, parent, moduleName) {
  "use strict";

  // Store the previous instance if it exists
  const previousModule = parent[moduleName];

  // Command queue for calls made before plugin is ready
  const commandQueue = [];
  let isReady = false;
  let readyCallbacks = [];

  // Create the main plugin object
  const Module = function () {
    // If called as function without new, return the instance
    if (!(this instanceof Module)) {
      return getInstance();
    }

    document.addEventListener("DOMContentLoaded", () => {
      this.init();
    });
  };

  // Singleton instance
  let instance = null;

  function getInstance() {
    if (!instance) {
      instance = new Module();
    }
    return instance;
  }

  // Core methods
  Module.prototype = {
    init: async function () {
      const self = this;

      try {
        const { Avo, identify } = await initAvo();
        self._Avo = Avo;
        self._identify = identify;
      } catch (error) {
        logger.error(`Telemetry:init:error`, error);
        throw error;
      }

      isReady = true;

      // Process command queue
      while (commandQueue.length > 0) {
        const item = commandQueue.shift();
        self[item.method].apply(self, item.args);
      }

      // Call all registered callbacks
      for (let i = 0; i < readyCallbacks.length; i++) {
        readyCallbacks[i].call(self);
      }

      // Dispatch custom event
      let event;
      if (typeof window.CustomEvent === "function") {
        event = new CustomEvent(`MiQ:${moduleName}:ready`, {
          detail: { instance: self },
        });
      }
      document.dispatchEvent(event);

      // Also dispatch a regular DOM event for frameworks that might be listening
      if (typeof window.Event === "function") {
        document.dispatchEvent(new Event(`MiQ${moduleName}Ready`));
      }
    },
    identify({ id, email }) {
      const self = this;
      self._identify({
        id,
        email,
      });
    },
    trackWebPageViewed(page, landingPageVariant) {
      const self = this;
      const url = window.location.href;
      try {
        self._Avo.webPageViewed({
          platform: "Web",
          webPage: page,
          url,
          landingPageVariant,
          impactClickId: getImpactClickId() ?? null,
          ...getTrackingAttributesFromUrl(),
        });
      } catch (e) {
        console.error(e);
      }
    },
    trackExperimentViewed(experiment) {
      const self = this;
      self._Avo.experimentViewed(experiment);
    },
    trackEvent(event, properties) {
      const self = this;
      self._Avo[event](properties);
    },
  };

  // Public API
  const publicAPI = {
    // Check if plugin is ready
    isReady: function () {
      return isReady;
    },

    // Execute when plugin is ready
    ready: function (callback) {
      if (isReady) {
        callback.call(getInstance());
      } else {
        readyCallbacks.push(callback);
      }
      return this;
    },

    readyAsync: function () {
      return new Promise((resolve) => {
        this.ready(resolve);
      });
    },

    // Queue a command or execute immediately if ready
    exec: function (methodName) {
      const args = Array.prototype.slice.call(arguments, 1);
      const instance = getInstance();

      if (!isReady) {
        commandQueue.push({
          method: methodName,
          args: args,
        });
        return null;
      }

      if (typeof instance[methodName] !== "function") {
        throw new Error("Method " + methodName + " not found");
      }

      return instance[methodName].apply(instance, args);
    },

    // Revert global variable to previous value and return this plugin
    noConflict: function () {
      parent[moduleName] = previousModule;
      return this;
    },

    // Get the plugin instance
    getInstance: getInstance,

    trackExperimentViewed: (...args) => {
      const instance = getInstance();
      return instance.trackExperimentViewed.call(instance, ...args);
    },

    trackWebPageViewed: (...args) => {
      const instance = getInstance();
      return instance.trackWebPageViewed.call(instance, ...args);
    },

    trackEvent: (...args) => {
      const instance = getInstance();
      return instance.trackEvent.call(instance, ...args);
    },

    identify: (...args) => {
      const instance = getInstance();
      return instance.identify.call(instance, ...args);
    },
  };

  // Expose the public API
  parent[moduleName] = publicAPI;

  // Auto-initialize
  getInstance();

  return publicAPI;
})(window, document, (window.MiQ = window.MiQ || {}), "Telemetry");
